import type { CSSProperties, FC, ReactNode } from 'react';
import {
  BarChartToolIcon,
  BuildingOfficeIcon,
  CalendarScheduleIcon,
  ClipboardTasksIcon,
  FolderArchiveDocIcon,
  GlobeEnuIcon,
  PlusSymbolIcon,
  ShareNodesIcon,
  UsersGroupIcon,
} from './DepartmentIcons';

interface DepartmentLabCabinetGridProps {
  roleTitle: string;
  onOpenWorkspace: (defaultView?: string) => void;
  onOpenSuperAi: () => void;
  onOpenRequestGateway: () => void;
  onOpenModal: (modalKey: string) => void;
}

type HubCard = {
  num: string;
  title: string;
  desc: string;
  accent: string;
  icon: ReactNode;
  onClick: () => void;
};

/**
 * DEPARTMENT_ASSISTANT / лаборант — отдельный набор карточек
 * (ограниченный административный контур, без title-swap с руководителем).
 */
export const DepartmentLabCabinetGrid: FC<DepartmentLabCabinetGridProps> = ({
  roleTitle,
  onOpenWorkspace,
  onOpenSuperAi,
  onOpenRequestGateway,
  onOpenModal,
}) => {
  const cards: HubCard[] = [
    {
      num: '01',
      title: 'Списки и состав кафедры',
      desc: 'Просмотр состава, показателей и административных списков подразделения.',
      accent: '#66b7e8',
      icon: <BuildingOfficeIcon className="w-5 h-5" />,
      onClick: () => onOpenWorkspace('staff'),
    },
    {
      num: '02',
      title: 'Реестр докторантов',
      desc: 'Годы, программы, руководители и статусы — поддержка учёта без изменений политик.',
      accent: '#49ddb7',
      icon: <UsersGroupIcon className="w-5 h-5" />,
      onClick: () => onOpenWorkspace('monitoring'),
    },
    {
      num: '03',
      title: 'Поручения и задачи',
      desc: 'Назначенные поручения кафедры, сроки исполнения и отметки о выполнении.',
      accent: '#e2ad3a',
      icon: <ClipboardTasksIcon className="w-5 h-5" />,
      onClick: () => onOpenWorkspace('tasks'),
    },
    {
      num: '04',
      title: 'Документы подразделения',
      desc: 'Кафедральные файлы, комплектность и загрузка по поручению руководителя.',
      accent: '#9e80ff',
      icon: <FolderArchiveDocIcon className="w-5 h-5" />,
      onClick: () => onOpenModal('files_hub'),
    },
    {
      num: '05',
      title: 'Связи и рассылки',
      desc: 'Контакты кафедры, группы и служебные сообщения.',
      accent: '#5da8ff',
      icon: <ShareNodesIcon className="w-5 h-5" />,
      onClick: () => onOpenModal('academic_connections'),
    },
    {
      num: '06',
      title: 'Опросы и формы',
      desc: 'Поддержка опросов, форм и исследовательских сервисов академии.',
      accent: '#ff7a64',
      icon: <BarChartToolIcon className="w-5 h-5" />,
      onClick: () => onOpenWorkspace('surveys'),
    },
    {
      num: '07',
      title: 'Встречи и дежурства',
      desc: 'Календарь встреч, дежурства и приглашения подразделения.',
      accent: '#41d2ff',
      icon: <CalendarScheduleIcon className="w-5 h-5" />,
      onClick: () => onOpenWorkspace('events'),
    },
    {
      num: '08',
      title: 'Официальные источники ЕНУ',
      desc: 'Регламенты, подтверждённые маршруты и справочные источники.',
      accent: '#49ddb7',
      icon: <GlobeEnuIcon className="w-5 h-5" />,
      onClick: () => onOpenModal('enu_sources'),
    },
  ];

  return (
    <div className="w-full role-cabinet-page">
      <div className="w-full py-8 space-y-6">
        <div className="role-hero" style={{ '--role-accent': '#66b7e8' } as CSSProperties}>
          <div className="min-w-0">
            <small>{roleTitle}</small>
            <h2>Кабинет · {roleTitle}</h2>
            <p>
              Ограниченный административный интерфейс: списки, поручения, документы, отчётность и
              встречи.
            </p>
          </div>
          <div className="role-hero-actions">
            <button type="button" className="role-btn" onClick={onOpenSuperAi}>
              Super AI
            </button>
            <button type="button" className="role-btn primary" onClick={onOpenRequestGateway}>
              <PlusSymbolIcon className="w-4 h-4 inline mr-1" />
              Создать заявку
            </button>
          </div>
        </div>

        <div className="owner-hub-top-actions">
          <button type="button" className="owner-hub-ai-action" onClick={onOpenSuperAi}>
            <span className="spark" aria-hidden="true">
              ✦
            </span>
            <span>
              <strong>Super AI</strong>
              <small>Помощник по функциям лаборанта кафедры</small>
            </span>
            <b>Открыть →</b>
          </button>
          <button type="button" className="owner-hub-create" onClick={onOpenRequestGateway}>
            + Создать заявку
          </button>
        </div>

        <section style={{ marginTop: 8 }} aria-label="Разделы лаборанта кафедры">
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Разделы лаборанта</h3>
            <p style={{ margin: '4px 0 0', color: '#8faebb', fontSize: 12 }}>
              8 рабочих контуров поддержки подразделения
            </p>
          </div>
          <div className="owner-hub-grid">
            {cards.map((card) => (
              <button
                key={card.num}
                type="button"
                onClick={card.onClick}
                className="owner-hub-card"
                style={{ '--action': card.accent } as CSSProperties}
              >
                <span className="owner-hub-index">{card.num}</span>
                <span className="owner-hub-icon" aria-hidden="true">
                  {card.icon}
                </span>
                <span className="owner-hub-copy">
                  <strong>{card.title}</strong>
                  <span>{card.desc}</span>
                </span>
                <span className="owner-hub-arrow" aria-hidden="true">
                  ›
                </span>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};
